/**
 * usePermissions Hook - Convenient permission checking
 * 
 * Provides permission checking utilities with fallback support.
 * Can be used inside or outside PermissionProvider.
 */

import { useContext } from 'react';
import PermissionContext from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_TEMPLATES } from '../utils/permissionConstants';
import { logger } from "../utils/logger";

/**
 * Hook for permission checking
 * 
 * Usage inside PermissionProvider:
 *   const { hasPermission, loading } = usePermissions();
 *   if (loading) return <Loading />;
 *   if (hasPermission('billing.view')) { ... }
 * 
 * Usage outside PermissionProvider (fallback to role-based):
 *   const { hasPermission } = usePermissions();
 *   if (hasPermission('admin.access')) { ... }
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  const { user } = useAuth();

  if (context) {
    return {
      permissions: context.permissions,
      loading: context.loading,
      error: context.error,
      hasPermission: context.hasPermission,
      hasAnyPermission: context.hasAnyPermission,
      hasAllPermissions: context.hasAllPermissions,
      refetchPermissions: context.refetchPermissions,
    };
  }

  const checkRoleHasPermission = (permissionKey: string): boolean => {
    if (user === null || user === false || !user.role) return false;
    // Check primary role
    const primaryPerms = ROLE_TEMPLATES[user.role as keyof typeof ROLE_TEMPLATES];
    if (primaryPerms && (primaryPerms as Record<string, boolean>)[permissionKey]) return true;
    // Check additional roles (UserRole many-to-many)
    const additionalRoles = (user as Record<string, unknown>).additional_roles as string[] | undefined;
    if (Array.isArray(additionalRoles)) {
      for (const role of additionalRoles) {
        const rolePerms = ROLE_TEMPLATES[role as keyof typeof ROLE_TEMPLATES];
        if (rolePerms && (rolePerms as Record<string, boolean>)[permissionKey]) return true;
      }
    }
    return false;
  };

  const checkRoleHasAnyPermission = (permissionKeys: string[]): boolean => {
    if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
      return true;
    }
    return permissionKeys.some(key => checkRoleHasPermission(key));
  };

  const checkRoleHasAllPermissions = (permissionKeys: string[]): boolean => {
    if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) {
      return true;
    }
    return permissionKeys.every(key => checkRoleHasPermission(key));
  };

  return {
    permissions: [],
    loading: false,
    error: null,
    hasPermission: checkRoleHasPermission,
    hasAnyPermission: checkRoleHasAnyPermission,
    hasAllPermissions: checkRoleHasAllPermissions,
    refetchPermissions: () => {
      logger.warn('refetchPermissions called outside PermissionProvider - no-op');
    },
  };
};

export default usePermissions;
